import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { learningAPI } from '../services/api';

const formatDateTime = (value) => {
  if (!value) return 'No deadline';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No deadline';
  return date.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatScore = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value) * 10) / 10}%` : '—');

function AssignmentCard({ assignment }) {
  const progress = assignment.progress || {};
  const attempts = progress.attempts || [];
  const canAttempt = progress.canLaunch === true;
  const isUpcoming = progress.available === false;
  const isArchived = assignment.status === 'archived' || progress.archived === true || progress.readOnly === true;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {progress.overdue && <Chip size="small" color="warning" label="Past deadline" />}
          {isArchived && <Chip size="small" variant="outlined" label="Archived · review only" />}
          {isUpcoming && <Chip size="small" color="info" variant="outlined" label="Upcoming" />}
          {progress.completed && <Chip size="small" color="success" label="Completed" />}
          {progress.exhausted && <Chip size="small" variant="outlined" label="Attempt limit reached" />}
          {canAttempt && <Chip size="small" color="primary" label="Available" />}
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>{assignment.case?.title || assignment.caseTitle}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {[assignment.case?.specialty, assignment.case?.presentingComplaint, assignment.case?.difficulty].filter(Boolean).join(' · ') || 'Clinical case'}
        </Typography>
        {assignment.instructions && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Tutor instructions</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{assignment.instructions}</Typography>
          </Paper>
        )}
        <Stack spacing={0.75} sx={{ mt: 2 }}>
          {isUpcoming && <Stack direction="row" spacing={1} alignItems="center"><EventRoundedIcon fontSize="small" color="info" /><Typography variant="body2">Available {formatDateTime(assignment.availableAt)}</Typography></Stack>}
          <Stack direction="row" spacing={1} alignItems="center"><EventRoundedIcon fontSize="small" color="action" /><Typography variant="body2">Due {formatDateTime(assignment.dueAt)}</Typography></Stack>
          <Stack direction="row" spacing={1} alignItems="center"><AssignmentTurnedInRoundedIcon fontSize="small" color="action" /><Typography variant="body2">{progress.attemptsRemaining ?? assignment.maxAttempts} of {assignment.maxAttempts} attempts remaining</Typography></Stack>
          {assignment.assignedBy?.name && <Typography variant="caption" color="text.secondary">Assigned by {assignment.assignedBy.name}</Typography>}
        </Stack>

        {attempts.length > 0 && (
          <Box sx={{ mt: 2.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Previous attempts</Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {attempts.map((attempt, index) => (
                <Stack key={attempt.historyId} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Attempt {attempts.length - index}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDateTime(attempt.completedAt)} · {formatScore(attempt.score)}</Typography>
                  </Box>
                  <Button component={RouterLink} to={`/history/${attempt.historyId}`} size="small" endIcon={<ArrowForwardRoundedIcon />}>View history</Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button component={RouterLink} to="/downloads" variant={canAttempt ? 'contained' : 'outlined'} disabled={!canAttempt}>
          {isArchived ? 'Archived' : isUpcoming ? 'Not available yet' : 'Open Virtual Medic app'}
        </Button>
      </CardActions>
    </Card>
  );
}

function AssignedCasesPage() {
  const [assignments, setAssignments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await learningAPI.listMyAssignments();
      setAssignments(response.data?.assignments || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load assigned cases.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const current = useMemo(() => assignments.filter((assignment) => (
    assignment.status === 'active'
      && !assignment.progress?.overdue
      && !assignment.progress?.exhausted
      && assignment.progress?.attemptsRemaining > 0
  )), [assignments]);
  const completedOrClosed = useMemo(() => assignments.filter((assignment) => (
    assignment.status === 'archived'
      || assignment.progress?.attempts?.length > 0
      || assignment.progress?.overdue
      || assignment.progress?.exhausted
  )), [assignments]);
  const visible = activeTab === 0 ? current : completedOrClosed;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 900 }}>Assigned cases</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
          Practice set by your tutors, with deadlines, attempt limits, and every completed attempt kept together.
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={loadAssignments}>Retry</Button>}>{error}</Alert>}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(_event, tab) => setActiveTab(tab)} sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`To practise (${current.length})`} />
          <Tab icon={<HistoryRoundedIcon />} iconPosition="start" label={`Completed & closed (${completedOrClosed.length})`} />
        </Tabs>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
          ) : visible.length ? (
            <Grid container spacing={2}>
              {visible.map((assignment) => <Grid item xs={12} md={6} lg={4} key={assignment.id}><AssignmentCard assignment={assignment} /></Grid>)}
            </Grid>
          ) : (
            <Box sx={{ py: 7, px: 3, textAlign: 'center' }}>
              <AssignmentTurnedInRoundedIcon sx={{ fontSize: 46, color: 'text.disabled' }} />
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 900 }}>{activeTab === 0 ? 'No cases waiting' : 'No completed or closed assignments yet'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {activeTab === 0 ? 'New tutor assignments will appear here and in your notifications.' : 'Completed attempts, missed deadlines, and closed assignments will stay visible here.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default AssignedCasesPage;
